// SegmentedControl — the exclusive-choice toggle primitive, pure.
//
// One choice out of a small set (Light/Dark, Focus/Review, Torus/Plain).
// Radiogroup semantics: exactly one segment is selected, arrow keys move
// between them, and the group is one tab stop. That behaviour — roving
// tabindex, arrow-key nav, role="radiogroup"/role="radio"/aria-checked — is
// the hard-a11y contract we do NOT hand-roll; we wrap @radix-ui/react-radio-
// group, which owns it. This file only styles and shapes the surface.
//
// Register (.claude/research/design/06): near-monochrome. The selected segment is NOT
// marked by colour — it reads via a lifted fill (an elevated surface + soft
// shadow, the border→elevation discipline) AND a heavier weight. Two non-
// colour channels, so selection survives SC 1.4.1 (colour is never the sole
// signal). No accent: an exclusive setting-toggle is not "the primary action
// in this view", so it stays neutral; spend the accent elsewhere.
//
// Pure/props-driven: `value` + `onChange` make it fully controlled — no
// internal selection state, every change reported UP. `options` is the data;
// deps arrive as props; no I/O, no domain knowledge. `ariaLabel` is required
// because a group of radios with no visible group label is unlabelled to AT,
// and there is no other place to name it (the segments have their own labels).
//
// Distinct-intent typing: `size` is genuine multi-value presentation config
// ("sm" | "md"), not a meaning-swapping boolean, so it stays one prop. All
// user-facing text (segment labels, the group label) is caller-owned — the
// primitive authors no string of its own, so it needs no i18n macro here.

import * as RadioGroup from "@radix-ui/react-radio-group";
import type { ReactNode } from "react";
import "./SegmentedControl.css";

type SegmentedControlSize = "sm" | "md";

// One selectable segment. `value` is the identity reported through onChange;
// `label` is the visible text; `icon` is an optional leading glyph (it should
// be aria-hidden — the label already names the segment).
type SegmentedOption = {
    readonly value: string;
    readonly label: string;
    readonly icon?: ReactNode;
};

export type SegmentedControlProps = {
    readonly options: readonly SegmentedOption[];
    readonly value: string;
    readonly onChange: (value: string) => void;
    readonly size?: SegmentedControlSize;
    // Names the group for assistive tech (SC 4.1.2 / 1.3.1). Required.
    readonly ariaLabel: string;
};

export const SegmentedControl = ({
    options,
    value,
    onChange,
    size = "md",
    ariaLabel,
}: SegmentedControlProps) => (
    <RadioGroup.Root
        className={`bilda-segmented bilda-segmented--${size}`}
        value={value}
        onValueChange={onChange}
        aria-label={ariaLabel}
        // Wrap arrow-key nav from last↔first, matching a compact toggle.
        loop
    >
        {options.map((option) => (
            <RadioGroup.Item
                key={option.value}
                value={option.value}
                className="bilda-segmented__item"
            >
                {option.icon != null ? (
                    <span className="bilda-segmented__icon" aria-hidden="true">
                        {option.icon}
                    </span>
                ) : null}
                {option.label}
            </RadioGroup.Item>
        ))}
    </RadioGroup.Root>
);
